import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Form.css';
import { toFullWidthKatakana, furiganaToFullWidth } from '../lib/kanaNormalize';
import { requiredFieldClass, requiredSelectClass, requiredFileWrapClass } from '../lib/requiredFieldClass';


const DRAFT_KEY = 'draft_family_change';

// 扶養家族の空データ（扶養家族1は常に表示する初期値）
const makeEmptyMember = () => ({
    livingStatus: '同居',
    isOverseas: false,
    relation: '',
    nameKanji: '',
    nameKana: '',
    birthDate: '',
    myNumber: '',
    image: '',
});

const FamilyChangeForm = ({ onBack, onNext, isFirstForm, isLastForm, isMultiForm, id, isDraft }) => {
    // フォームデータの状態を管理（扶養家族1は最初から表示）
    const [changeType, setChangeType] = useState('');
    const [familyMembers, setFamilyMembers] = useState([makeEmptyMember()]);

    // マイナンバー画像は家族ごとに別管理（キー = 家族の index）
    const [imageFiles, setImageFiles] = useState({});
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const isComposing = useRef(false);

    React.useEffect(() => {
        try {
            const saved = localStorage.getItem(DRAFT_KEY);
            if (saved) {
                const parsed = JSON.parse(saved) || {};
                // 以前の下書きに残っている家族のマイナンバーは読み込まずに消す（docs/spec_mynumber.md 9 章）
                if (Array.isArray(parsed.familyMembers)) {
                    if (parsed.familyMembers.some((m) => m && m.myNumber)) {
                        localStorage.setItem(DRAFT_KEY, JSON.stringify({
                            ...parsed,
                            familyMembers: parsed.familyMembers.map(({ myNumber, ...m }) => m),
                        }));
                    }
                    parsed.familyMembers = parsed.familyMembers.map(({ myNumber, ...m }) => ({ ...m, myNumber: '' }));
                }
                setChangeType(parsed.changeType || '');
                // 下書きに家族がいればそれを、無ければ扶養家族1を1件表示
                setFamilyMembers(
                    Array.isArray(parsed.familyMembers) && parsed.familyMembers.length > 0
                        ? parsed.familyMembers
                        : [makeEmptyMember()]
                );
            }
        } catch (_) {}
    }, []);

    const handleSaveDraft = () => {
        // 家族のマイナンバーは端末（localStorage）に保存しない（docs/spec_mynumber.md 9 章）
        const membersWithoutNumber = familyMembers.map(({ myNumber, ...m }) => m);
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ changeType, familyMembers: membersWithoutNumber }));
        alert('一時保存しました。（マイナンバーと画像は安全のため保存されません）');
    };

    const handleDeleteDraft = async () => {
        const confirmDelete = window.confirm("この下書き（一時保存データ）を削除してもよろしいですか？\n※削除すると元に戻せません。");
        if (!confirmDelete) return;

        setLoading(true);
        try {
            if (id) {
                await api.delete(`/api/applications/drafts/${id}/`);
            }
            // ローカルでも削除
            localStorage.removeItem(DRAFT_KEY);
            alert('下書きを削除しました。');
            navigate('/applications');
        } catch (error) {
            console.error('下書きの削除に失敗:', error);
            alert('下書きの削除に失敗しました。もう一度お試しください。');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => { if (onBack) onBack(); else navigate('/applications'); };

    // ユーザーが変更の種類（増加/減少）を選択したときに処理するための関数
    const handleRadioChange = (e) => {
        setChangeType(e.target.value);
    };

    // 新規メンバーを追加
    const handleAddMember = () => {
        setFamilyMembers(prevMembers => [...prevMembers, makeEmptyMember()]);
    };

    // メンバーを削除（追加を間違えたとき用。扶養家族1は残す）
    const handleRemoveMember = (index) => {
        setFamilyMembers(prevMembers => prevMembers.filter((_, i) => i !== index));
        // 画像も index を詰め直す
        setImageFiles(prev => {
            const next = {};
            Object.keys(prev).map(Number).forEach(k => {
                if (k < index) next[k] = prev[k];
                else if (k > index) next[k - 1] = prev[k];
            });
            return next;
        });
    };

    const handleMemberInputChange = (e, index) => {
        const { name, value } = e.target;
        const newMembers = [...familyMembers];
        if (name === 'nameKana') {
            if (isComposing.current) {
                newMembers[index][name] = value;
            } else {
                newMembers[index][name] = toFullWidthKatakana(furiganaToFullWidth(value));
            }
        } else {
            newMembers[index][name] = value;
        }
        setFamilyMembers(newMembers);
    };

    const handleCompositionEnd = (e, index) => {
        isComposing.current = false;
        setTimeout(() => handleMemberInputChange(e, index), 50);
    };

    const handleFileChange = (e, index) => {
        const file = e.target.files[0];
        setImageFiles(prev => ({ ...prev, [index]: file }));
    };

    // フォームが送信された時の処理
    const handleSubmit = async (e) => {
        e.preventDefault();

        const isConfirmed = window.confirm("家族異動申請を送信してもよろしいですか？");
    
            if (!isConfirmed) {
                return; 
            }
        if (familyMembers.length === 0 && changeType === '増加') {
            alert("扶養家族を追加してください。");
            return;
        }

        const familyMembersCount = familyMembers.length;

        // マイナンバー画像を含むため multipart/form-data で送信する
        // familyMembers は JSON 文字列にし、画像は image_0, image_1... で各家族に対応させる
        const membersForSubmit = familyMembers.map((m) => ({
            livingStatus: m.livingStatus,
            isOverseas: !!m.isOverseas,
            relation: m.relation,
            nameKanji: m.nameKanji,
            nameKana: m.nameKana,
            birthDate: m.birthDate,
            myNumber: m.myNumber || '',
        }));

        const fd = new FormData();
        fd.append('changeType', changeType);
        fd.append('familyMembersCount', String(familyMembersCount));
        fd.append('familyMembers', JSON.stringify(membersForSubmit));
        familyMembers.forEach((_, i) => {
            if (imageFiles[i]) fd.append(`image_${i}`, imageFiles[i]);
        });

        try {
            const response = await api.post('/api/forms/submit_family_change/', fd);
            localStorage.removeItem(DRAFT_KEY);
            alert('家族異動届が正常に提出されました。');
            navigate('/applications');
        } catch (error) {
            console.error('Submission failed:', error);
            let errorMessage = '申請に失敗しました。もう一度お試しください。';

            //err
            if (error.response && error.response.data) {             
                errorMessage += " 詳細: " + JSON.stringify(error.response.data);
            }
            
            alert(errorMessage);
            
        }
    };

    return (
        <div className="container form-container">
            <div>
                <h3>家族異動届</h3>
                <h4>家族の増・減</h4>
            </div>
            

            <form onSubmit={handleSubmit}>
                <div className="mb-3">
                    <label className="form-label">異動区分</label><br />
                    <div className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="changeType" value="増" id="change_increase" onChange={handleRadioChange} required />
                        <label className="form-check-label" htmlFor="change_increase">増</label>
                    </div>
                    <div className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="changeType" value="減" id="change_decrease" onChange={handleRadioChange} required />
                        <label className="form-check-label" htmlFor="change_decrease">減</label>
                    </div>
                </div>

                {/* 家族のメンバーの動的レンダリング部分 */}
                <div id="family-members-container">
                    {familyMembers.map((member, index) => (
                        <div key={index} className="member-input-group mt-4 p-3 border rounded">
                            <div className="d-flex" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                <h6 className="text-info" style={{ margin: 0 }}>扶養家族 {index + 1}</h6>
                                {familyMembers.length > 1 && (
                                    <button
                                        type="button"
                                        className="btn-remove-member"
                                        onClick={() => handleRemoveMember(index)}
                                    >
                                        削除
                                    </button>
                                )}
                            </div>
                            <div className="mb-3">
                                <label className="form-label">居住区分</label>
                                    <div className="d-flex gap-3">
                                        <div className="form-check">
                                            <input 
                                                className="form-check-input" 
                                                type="radio" 
                                                name={`livingStatus${index}`} 
                                                id={`dōkyo${index}`}
                                                value="同居" 
                                                checked={member.livingStatus === '同居'} 
                                                onChange={(e) => {
                                                    const newMembers = [...familyMembers];
                                                    newMembers[index].livingStatus = e.target.value;
                                                    setFamilyMembers(newMembers);
                                                }}
                                            />
                                            <label className="form-check-label" htmlFor={`dōkyo${index}`}>同居</label>
                                        </div>
                                        <div className="form-check">
                                            <input 
                                                className="form-check-input" 
                                                type="radio" 
                                                name={`livingStatus${index}`} 
                                                id={`bekkyo${index}`}
                                                value="別居" 
                                                checked={member.livingStatus === '別居'} 
                                                onChange={(e) => {
                                                    const newMembers = [...familyMembers];
                                                    newMembers[index].livingStatus = e.target.value;
                                                    setFamilyMembers(newMembers);
                                                }}
                                            />
                                            <label className="form-check-label" htmlFor={`bekkyo${index}`}>別居</label>
                                        </div>
                                    </div>
                                <label htmlFor={`relation${index}`} className="form-label">続柄</label>
                                <select id={`relation${index}`} name="relation" className={requiredSelectClass(member.relation)} value={member.relation} onChange={(e) => handleMemberInputChange(e, index)} required>
                                    <option value="">選択してください</option>
                                    <option value="夫">夫</option>
                                    <option value="妻">妻</option>
                                    <option value="子">子</option>
                                    <option value="父">父</option>
                                    <option value="母">母</option>
                                    <option value="孫">孫</option>
                                    <option value="祖父">祖父</option>
                                    <option value="祖母">祖母</option>
                                    <option value="兄">兄</option>
                                    <option value="弟">弟</option>
                                    <option value="姉">姉</option>
                                    <option value="妹">妹</option>
                                    <option value="義父">義父</option>
                                    <option value="義母">義母</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label htmlFor={`nameKanji${index}`} className="form-label">氏名（漢字）</label>
                                <input type="text" id={`nameKanji${index}`} name="nameKanji" className={requiredFieldClass(member.nameKanji)} value={member.nameKanji} onChange={(e) => handleMemberInputChange(e, index)} required />
                            </div>
                            <div className="mb-3">
                                <label htmlFor={`nameKana${index}`} className="form-label">氏名（フリガナ）</label>
                                <input
                                    type="text"
                                    id={`nameKana${index}`}
                                    name="nameKana"
                                    className={requiredFieldClass(member.nameKana)}
                                    value={member.nameKana}
                                    onChange={(e) => handleMemberInputChange(e, index)}
                                    onCompositionStart={() => { isComposing.current = true; }}
                                    onCompositionEnd={(e) => handleCompositionEnd(e, index)}
                                    required
                                    placeholder="カタカナで入力"
                                />
                            </div>
                            <div className="mb-3">
                                <label htmlFor={`birthDate${index}`} className="form-label">生年月日</label>
                                <input type="date" id={`birthDate${index}`} name="birthDate" className={requiredFieldClass(member.birthDate)} value={member.birthDate} onChange={(e) => handleMemberInputChange(e, index)} required />
                            </div>
                            <div className="mb-3">
                                <label htmlFor={`myNumber${index}`} className="form-label">マイナンバー</label>
                                <input
                                    type="text"
                                    id={`myNumber${index}`}
                                    name="myNumber"
                                    className={requiredFieldClass(member.myNumber)}
                                    value={member.myNumber}
                                    onChange={(e) => handleMemberInputChange(e, index)}
                                    maxLength="12"
                                    inputMode="numeric"
                                    pattern="\d{12}"
                                    placeholder="例: 123-45-6789"
                                    required
                                />
                                <div className={requiredFileWrapClass(imageFiles[index])}>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={(e) => handleFileChange(e, index)}
                                        required
                                    />
                                </div>
                                <small className="text-muted">※マイナンバーカードの表面をアップロードしてください。</small>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mb-3">
                    <button type="button" className="btn-info" onClick={handleAddMember}>扶養家族を追加</button>
                </div>
            
            <div className="button-group">
                <button type="submit" className="btn-submit">申請</button>
                <button type="button" className="btn-draft" onClick={handleSaveDraft}>一時保存</button>
                {((id && isDraft) || localStorage.getItem(DRAFT_KEY)) && <button type="button" className="btn-delete-draft" onClick={handleDeleteDraft} disabled={loading}>下書削除</button>}
                <button type="button" className="btn-back" onClick={handleBack}>{onBack && !isFirstForm ? '前へ' : '戻る'}</button>
                {isMultiForm && onNext && <button type="button" className="btn-nav" onClick={onNext}>{isLastForm ? '完了' : '次へ'}</button>}
            </div>
            </form>
        </div>
    );
};

export default FamilyChangeForm;